import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      content: 'hello@meowcentral.fun',
      link: 'mailto:hello@meowcentral.fun',
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      content: '+1 (555) MEOW-CAT',
      link: 'tel:+15556369228',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Visit Us',
      content: '123 Purr Street, Cat City, CC 90210',
      link: '#',
    },
  ];

  const socialLinks = [
    { icon: 'ri-facebook-fill', name: 'Facebook', color: 'bg-blue-600', url: 'https://www.facebook.com/meowcentral' },
    { icon: 'ri-instagram-fill', name: 'Instagram', color: 'bg-pink-600', url: 'https://www.instagram.com/meowcentral' },
    { icon: 'ri-twitter-x-fill', name: 'Twitter', color: 'bg-gray-800', url: 'https://twitter.com/meowcentral' },
    { icon: 'ri-youtube-fill', name: 'YouTube', color: 'bg-red-600', url: 'https://www.youtube.com/@meowcentral' },
    { icon: 'ri-tiktok-fill', name: 'TikTok', color: 'bg-gray-900', url: 'https://www.tiktok.com/@meowcentral' },
  ];

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact MeowCentral',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
    description: 'Get in touch with the MeowCentral team. We love hearing from cat lovers — questions, partnerships, feedback, or just sharing your cat stories.',
    mainEntity: {
      '@type': 'Organization',
      name: 'MeowCentral',
      email: 'hello@meowcentral.fun',
      telephone: '+1-555-636-9228',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '123 Purr Street',
        addressLocality: 'Cat City',
        postalCode: '90210',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'hello@meowcentral.fun',
        telephone: '+1-555-636-9228',
        availableLanguage: 'English',
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-teal-50">
      <SEOHead
        title="Contact MeowCentral - Get In Touch With Our Cat-Loving Team"
        description="Have questions, partnership ideas, or cat stories to share? Contact MeowCentral's team. We respond within 24 hours and love hearing from fellow cat lovers!"
        keywords="contact MeowCentral, cat website support, cat community contact, cat partnership inquiry"
        canonical="/contact"
        schema={contactSchema}
      />
      <Navbar />
      
      {/* Header Section */}
      <div className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
            Get In Touch 💌
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Have questions, suggestions, or just want to share your cat stories? 
            We'd love to hear from you!
          </p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="px-4 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {contactInfo.map((info, index) => (
              <a
                key={index}
                href={info.link}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center group cursor-pointer"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${info.icon} text-3xl text-teal-500 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{info.title}</h3>
                <p className="text-gray-600">{info.content}</p>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Send Us a Message</h2>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl"></i>
                  <span>Thank you! Your message has been sent successfully.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-400 focus:outline-none transition-colors"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-400 focus:outline-none transition-colors"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-400 focus:outline-none transition-colors"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Customer Support</option>
                    <option value="partnership">Partnership</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-400 focus:outline-none transition-colors resize-none"
                    placeholder="Tell us what's on your mind..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-500 text-white py-4 rounded-xl font-bold text-lg hover:bg-teal-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Additional Info */}
            <div className="space-y-8">
              {/* Image */}
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/abaaccbf4a797187d158d965155e1054.jpg"
                  alt="Contact Us"
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    We're Here to Help! 🐾
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Whether you have a question about our videos, products, or just want to share 
                    your adorable cat photos with us, we're always happy to hear from fellow cat lovers!
                  </p>
                  <p className="text-gray-600">
                    Our team typically responds within 24 hours during business days.
                  </p>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Follow Us</h3>
                <p className="text-gray-600 mb-6">
                  Join our community on social media for daily doses of cuteness!
                </p>
                <div className="flex flex-wrap gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className={`${social.color} text-white w-12 h-12 rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer`}
                      aria-label={social.name}
                    >
                      <i className={`${social.icon} text-xl`}></i>
                    </a>
                  ))}
                </div>
              </div>

              {/* FAQ Link */}
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl shadow-xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Quick Answers</h3>
                <p className="mb-6 opacity-90">
                  Looking for immediate answers? Check out our FAQ section for common questions.
                </p>
                <button className="bg-white text-teal-600 px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                  Visit FAQ
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}